// Hideo - the page split into separate A4 sheets (from 768 px): a layer behind #scriptBody draws one sheet per page,
// with a gap of desk between them, and a block that would end in a page's bottom margin moves to the top of the next
// sheet. The moves are rules of a <style> of their own (#scriptBody > :nth-child(n) { margin-top }), never written in
// the page, so the saved HTML does not change. Only the page's top-level blocks move, whole: a block taller than a
// page stays where it is. Laid out again on every change of the page (MutationObserver) or of its size
// (ResizeObserver: typing, fonts, images, window width), at most once a frame. On the phone the page is one sheet.

export const PAGE_H = 1123   // A4 height at 96 dpi (px)
export const PAGE_GAP = 24   // desk between two sheets
export const PAGE_MARGIN = 72 // top and bottom margin inside a sheet (same as the side margins)
const PERIOD = PAGE_H + PAGE_GAP

/** Natural position of a top-level block: top and bottom (px from the top of the page), gap above it */
export interface BlockBox { top: number; bottom: number; gap: number }

/** Where blocks go: for each block that moves, its index and its new margin-top; the number of sheets */
export function paginate(blocks: BlockBox[]): { moves: [index: number, marginTop: number][]; pages: number } {
  const moves: [number, number][] = []
  let shift = 0, last = 0
  blocks.forEach((b, i) => {
    const top = b.top + shift, bottom = b.bottom + shift, height = b.bottom - b.top
    const k = Math.floor(top / PERIOD), limit = k * PERIOD + PAGE_H - PAGE_MARGIN
    if (i > 0 && height > 0 && bottom > limit && top > k * PERIOD + PAGE_MARGIN + 1 && height <= PAGE_H - 2 * PAGE_MARGIN) {
      const d = (k + 1) * PERIOD + PAGE_MARGIN - top
      moves.push([i, b.gap + d]); shift += d
    }
    last = Math.max(last, b.bottom + shift)
  })
  return { moves, pages: Math.max(1, Math.ceil((last + PAGE_MARGIN + PAGE_GAP) / PERIOD)) }
}

let css: HTMLStyleElement | null = null
let sheets: HTMLElement | null = null
let queued = false

function layout(body: HTMLElement): void {
  queued = false
  if (!css || !sheets) return
  if (!body.offsetParent || !window.matchMedia('(min-width:768px)').matches) { css.textContent = ''; sheets.replaceChildren(); return }
  // Measure without the moves, keeping the page's height (a shorter page would make the scroll jump)
  css.textContent = `#scriptBody{min-height:${body.offsetHeight}px!important}`
  const origin = body.getBoundingClientRect().top
  let prev = PAGE_MARGIN
  const boxes = Array.from(body.children).map(el => {
    const r = el.getBoundingClientRect(), top = r.top - origin
    const box = { top, bottom: r.bottom - origin, gap: Math.max(0, top - prev) }
    if (r.height > 0) prev = box.bottom
    return box
  })
  const { moves, pages } = paginate(boxes)
  css.textContent = moves.map(([i, m]) => `#scriptBody>:nth-child(${i + 1}){margin-top:${m}px!important}`).join('') +
    `#scriptBody{min-height:${pages * PERIOD - PAGE_GAP}px!important}`
  if (sheets.childElementCount !== pages) {
    sheets.replaceChildren(...Array.from({ length: pages }, (_, k) => {
      const s = document.createElement('div'); s.className = 'script-sheet'; s.style.top = k * PERIOD + 'px'; return s
    }))
  }
}

function queue(body: HTMLElement): void {
  if (queued) return
  queued = true
  requestAnimationFrame(() => layout(body))
}

/** Adds the sheets layer and the observers (src/features/editor/surface/index.ts, once) */
export function installPages(body: HTMLElement): void {
  const wrap = body.parentElement
  if (!wrap || typeof ResizeObserver === 'undefined' || typeof requestAnimationFrame === 'undefined') return
  css = document.createElement('style'); css.id = 'scriptPagesCss'; document.head.appendChild(css)
  sheets = document.createElement('div'); sheets.className = 'script-sheets'; sheets.setAttribute('aria-hidden', 'true')
  wrap.insertBefore(sheets, body)
  new ResizeObserver(() => queue(body)).observe(body)
  new MutationObserver(() => queue(body)).observe(body, { childList: true, subtree: true, characterData: true, attributes: true })
}
